import { useNavigate } from 'react-router-dom';

interface StateCityGridProps {
  stateData: {
    name: string;
    slug: string;
    topRate?: string | number;
    cities: {
      slug: string;
      name: string;
      population?: string | number;
    }[];
  };
  calculatorCities: string[];
  stateSlug: string;
}

/**
 * Renders a grid of cities for a given state. Each city links to its salary
 * calculator when available. The component gracefully handles missing data
 * and prevents navigation for cities without a calculator.
 */
const StateCityGrid = ({
  stateData,
  calculatorCities,
  stateSlug,
}: StateCityGridProps) => {
  const navigate = useNavigate();

  // If there are no cities, render nothing – this prevents runtime errors.
  if (!stateData?.cities?.length) return null;

  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          <strong>Popular Cities in {stateData.name}</strong>
        </h2>

        <p className="text-gray-500 text-sm mb-8">
          Browse salary calculators for the most popular cities. Cities with active
          calculators are highlighted.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stateData.cities.map((city) => {
            const hasCalculator = calculatorCities.includes(city.slug);
            const cityUrl = hasCalculator ? `/usa/${stateSlug}/${city.slug}` : '#';

            // Build the image URL safely – encode the query string to avoid
            // malformed URLs and keep the JSX clean.
            const imageUrl = `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%60$%7Bcity.name%7D%20$%7BstateData.name%7D%20cityscape%20skyline%20downtown%20modern%20architecture%60%20%20%20%20%20%20%20%20%20%20%20%20%20%29%7D&width=600&height=400&seq=city-${city.slug}-${stateData.slug}&orientation=landscape`;

            return (
              <article
                key={city.slug}
                className={`bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 transition-all ${
                  hasCalculator
                    ? 'hover:shadow-lg hover:border-teal-300 cursor-pointer'
                    : 'opacity-75'
                }`}
              >
                <a
                  href={cityUrl}
                  onClick={(e) => {
                    // Prevent navigation for cities without a calculator.
                    if (!hasCalculator) {
                      e.preventDefault();
                      return;
                    }

                    // Use react‑router navigation instead of a full page reload.
                    e.preventDefault();
                    navigate(cityUrl);
                  }}
                  className={hasCalculator ? 'cursor-pointer' : 'cursor-default'}
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={`${city.name}, ${stateData.name}`}
                      title={`${city.name} salary calculator`}
                      className="w-full h-full object-cover object-top"
                      width="600"
                      height="400"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">{city.name}</h3>
                      {city.population && (
                        <p className="text-gray-200 text-sm">{city.population}</p>
                      )}
                    </div>
                    {hasCalculator && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-teal-500 rounded-full text-white text-xs font-bold whitespace-nowrap">
                          Calculator Available
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>
                        <i className="ri-map-pin-line mr-1"></i>
                        {stateData.name}
                      </span>
                      {stateData.topRate && (
                        <span className="font-medium text-gray-900">
                          Top Rate: {stateData.topRate}
                        </span>
                      )}
                    </div>
                  </div>
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StateCityGrid;
